/**
 * Ticket search (21 Oct): the search criteria, as the page holds them and as
 * the API takes them, plus the small pure helpers the page needs (saved
 * searches, the quick filter, the CSV export).
 *
 * No `@/` imports and no JSX, so `tests/ticket-search.test.mjs` runs it with
 * plain `node --test`.
 */

export type SearchKind = "all" | "incident" | "request";
export type StatusGroup = "any" | "open" | "closed";

/** A person picked from the directory: the RecId sent, the name shown. */
export interface PersonChoice {
  id: string;
  name: string;
}

/** Every pick-list filter, by its API name. */
export const LIST_FILTERS = [
  "statuses",
  "teams",
  "priorities",
  "categories",
  "services",
  "offerings",
  "sources",
  "builds",
  "entities",
  "systems",
  "projects",
] as const;
export type ListFilter = (typeof LIST_FILTERS)[number];

/** The date ranges, by the API's `<name>_from` / `<name>_to`. */
export const DATE_RANGES = ["created", "resolved", "next_step"] as const;
export type DateRange = (typeof DATE_RANGES)[number];

export interface Criteria {
  kind: SearchKind;
  keywords: string;
  searchNotes: boolean;
  number: string;
  statusGroup: StatusGroup;
  lists: Record<ListFilter, string[]>;
  owner: PersonChoice | null;
  customer: PersonChoice | null;
  /** ISO dates (YYYY-MM-DD) or null. */
  dates: Record<DateRange, { from: string | null; to: string | null }>;
}

export function emptyCriteria(): Criteria {
  return {
    kind: "all",
    keywords: "",
    searchNotes: false,
    number: "",
    statusGroup: "any",
    lists: Object.fromEntries(LIST_FILTERS.map((name) => [name, []])) as unknown as Record<
      ListFilter,
      string[]
    >,
    owner: null,
    customer: null,
    dates: Object.fromEntries(
      DATE_RANGES.map((name) => [name, { from: null, to: null }]),
    ) as unknown as Criteria["dates"],
  };
}

/** The backend's ticket number rule: digits, optionally "#", "SR", "IN"/"INC" first. */
const NUMBER = /^\s*#?\s*(?:sr|inc?)?\s*-?\s*\d{1,9}\s*$/i;

export function numberIsValid(text: string): boolean {
  return text.trim() === "" || NUMBER.test(text);
}

/** The API's query parameters; empty filters are left out. */
export function toQuery(criteria: Criteria): Record<string, string | boolean | string[]> {
  const query: Record<string, string | boolean | string[]> = {};
  if (criteria.kind !== "all") {
    query.kind = criteria.kind;
  }
  const keywords = criteria.keywords.trim();
  if (keywords) {
    query.keywords = keywords;
    if (criteria.searchNotes) {
      query.search_notes = true;
    }
  }
  if (criteria.number.trim()) {
    query.number = criteria.number.trim();
  }
  if (criteria.statusGroup !== "any") {
    query.status_group = criteria.statusGroup;
  }
  for (const name of LIST_FILTERS) {
    if (criteria.lists[name].length > 0) {
      query[name] = [...criteria.lists[name]];
    }
  }
  if (criteria.owner) {
    query.owner_id = criteria.owner.id;
  }
  if (criteria.customer) {
    query.customer_id = criteria.customer.id;
  }
  for (const name of DATE_RANGES) {
    const { from, to } = criteria.dates[name];
    if (from) {
      query[`${name}_from`] = from;
    }
    if (to) {
      query[`${name}_to`] = to;
    }
  }
  return query;
}

/** How many filters are set (keywords and number count as one each). */
export function activeFilterCount(criteria: Criteria): number {
  const query = toQuery(criteria);
  delete query.search_notes;
  return Object.keys(query).length;
}

const ISO_DAY = /^\d{4}-\d{2}-\d{2}$/;

function strings(value: unknown): string[] {
  return Array.isArray(value)
    ? value.filter((v): v is string => typeof v === "string" && v.trim() !== "").slice(0, 25)
    : [];
}

function person(value: unknown): PersonChoice | null {
  if (value && typeof value === "object") {
    const { id, name } = value as Record<string, unknown>;
    if (typeof id === "string" && id && typeof name === "string") {
      return { id, name };
    }
  }
  return null;
}

function day(value: unknown): string | null {
  return typeof value === "string" && ISO_DAY.test(value) ? value : null;
}

/** Criteria read back from storage; anything unexpected becomes "no filter". */
export function parseCriteria(raw: unknown): Criteria {
  const base = emptyCriteria();
  if (!raw || typeof raw !== "object") {
    return base;
  }
  const value = raw as Record<string, unknown>;
  const lists = (value.lists ?? {}) as Record<string, unknown>;
  const dates = (value.dates ?? {}) as Record<string, Record<string, unknown> | undefined>;
  return {
    kind: value.kind === "incident" || value.kind === "request" ? value.kind : "all",
    keywords: typeof value.keywords === "string" ? value.keywords.slice(0, 200) : "",
    searchNotes: value.searchNotes === true,
    number: typeof value.number === "string" ? value.number.slice(0, 20) : "",
    statusGroup:
      value.statusGroup === "open" || value.statusGroup === "closed" ? value.statusGroup : "any",
    lists: Object.fromEntries(
      LIST_FILTERS.map((name) => [name, strings(lists[name])]),
    ) as unknown as Criteria["lists"],
    owner: person(value.owner),
    customer: person(value.customer),
    dates: Object.fromEntries(
      DATE_RANGES.map((name) => [name, { from: day(dates[name]?.from), to: day(dates[name]?.to) }]),
    ) as unknown as Criteria["dates"],
  };
}

// ── Saved searches (this browser, per signed-in person) ─────────────────────

export interface SavedSearch {
  name: string;
  criteria: Criteria;
}

/** At most this many named searches are kept. */
export const MAX_SAVED = 12;

export function storageKeys(email: string): { last: string; saved: string } {
  const who = email.trim().toLowerCase();
  return { last: `helpdesk.ticketSearch.last:${who}`, saved: `helpdesk.ticketSearch.saved:${who}` };
}

export function parseSaved(raw: string | null): SavedSearch[] {
  if (!raw) {
    return [];
  }
  try {
    const value: unknown = JSON.parse(raw);
    if (!Array.isArray(value)) {
      return [];
    }
    return value
      .filter(
        (item): item is { name: string; criteria: unknown } =>
          !!item &&
          typeof item === "object" &&
          typeof (item as { name?: unknown }).name === "string",
      )
      .map((item) => ({ name: item.name.slice(0, 60), criteria: parseCriteria(item.criteria) }))
      .slice(0, MAX_SAVED);
  } catch {
    return [];
  }
}

/** Add or replace (by name, ignoring case) a saved search; newest first. */
export function upsertSaved(list: SavedSearch[], name: string, criteria: Criteria): SavedSearch[] {
  const clean = name.trim().slice(0, 60);
  if (!clean) {
    return list;
  }
  const rest = list.filter((item) => item.name.toLowerCase() !== clean.toLowerCase());
  return [{ name: clean, criteria }, ...rest].slice(0, MAX_SAVED);
}

export function removeSaved(list: SavedSearch[], name: string): SavedSearch[] {
  return list.filter((item) => item.name !== name);
}

// ── Results ────────────────────────────────────────────────────────────────

/** Rows whose given fields contain every word of `text` (case-insensitive). */
export function quickFilter<Row>(
  rows: readonly Row[],
  text: string,
  fields: (row: Row) => (string | null | undefined)[],
): Row[] {
  const words = text.toLowerCase().split(/\s+/).filter(Boolean);
  if (words.length === 0) {
    return [...rows];
  }
  return rows.filter((row) => {
    const haystack = fields(row)
      .filter((v): v is string => typeof v === "string")
      .join(" ")
      .toLowerCase();
    return words.every((word) => haystack.includes(word));
  });
}

export interface CsvColumn<Row> {
  label: string;
  value: (row: Row) => string | number | null | undefined;
}

function csvCell(value: string | number | null | undefined): string {
  if (value === null || value === undefined) {
    return "";
  }
  const text = String(value);
  // A leading = + - @ (or tab / CR) would be read by Excel as a formula.
  const safe = typeof value === "string" && /^[=+\-@\t\r]/.test(text) ? `'${text}` : text;
  return /[",\n\r;]/.test(safe) ? `"${safe.replace(/"/g, '""')}"` : safe;
}

/** CSV for Excel: a header row, CRLF lines and a byte-order mark (UTF-8). */
export function rowsToCsv<Row>(rows: readonly Row[], columns: readonly CsvColumn<Row>[]): string {
  const lines = [columns.map((c) => csvCell(c.label)).join(",")];
  for (const row of rows) {
    lines.push(columns.map((c) => csvCell(c.value(row))).join(","));
  }
  return `\uFEFF${lines.join("\r\n")}\r\n`;
}

/** "ticket-search-2026-10-21.csv". */
export function csvFileName(now: Date): string {
  return `ticket-search-${now.toISOString().slice(0, 10)}.csv`;
}
